import { isArray } from 'lodash-es';
import { Tooltip } from 'antd';

// 类型定义
interface QuestionnaireItem {
  [key: string]: any;
}

// 样式常量
const CELL_STYLE = {
  marginBottom: '4px',
  height: '26px',
  lineHeight: '26px',
  padding: '0 6px',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
};

// 通用渲染函数
const renderQuestionnaireField = (
  questionnaireList: QuestionnaireItem[],
  field: string,
  isNegativeTag = false,
  width: number
) => {
  if (!isArray(questionnaireList) || questionnaireList.length === 0) {
    return '';
  }

  return (
    <div>
      {questionnaireList.map((item, index) => {
        let displayText = '';

        if (isNegativeTag) {
          // 处理negativeTag对象格式
          const negativeTag = item[field] || {};
          displayText = Object.keys(negativeTag).length > 0
            ? Object.entries(negativeTag)
              .map(([tag, count]) => `${tag}：${count}`)
              .join('，')
            : '';
        } else {
          // 处理普通字段
          displayText = item[field] ?? '';
        }

        return (
          <Tooltip key={index} title={displayText} placement="topLeft">
            <div style={{ ...CELL_STYLE, width }}>
              {displayText}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

// 预定义渲染函数 - 避免每次重新创建
const renderIssue = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'issue', false, 300);

const renderIssueCategory = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'issueCategory', false, 200);

const renderQuestionnaireName = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'questionnaireName', false, 200);

const renderScoreSize = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'scoreSize', false, 150);

const renderScore1Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score1Size', false, 120);

const renderScore2Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score2Size', false, 120);

const renderScore3Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score3Size', false, 120);

const renderScore4Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score4Size', false, 120);

const renderScore5Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score5Size', false, 120);

const renderScore6Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score6Size', false, 120);

const renderScore7Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score7Size', false, 120);

const renderScore8Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score8Size', false, 120);

const renderScore9Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score9Size', false, 120);

const renderScore10Size = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'score10Size', false, 120);

const renderNegativeTag = (questionnaireList: QuestionnaireItem[]) =>
  renderQuestionnaireField(questionnaireList, 'negativeTag', true, 300);

export const config = {
  rowSelection: false,
  tableType: 'antd',
  virtual: true,
  columns: [
    {
      title: '分公司',
      dataIndex: 'companyName',
      width: 160,
      fixed: 'left',
    },
    {
      title: '推送量',
      dataIndex: 'questionnaireSendSize',
      width: 160,
    },
    {
      title: '问卷回收数量',
      dataIndex: 'questionnaireSize',
      width: 160,
    },
    {
      title: '满意数',
      dataIndex: 'satisfactionSize',
      width: 100,
    },
    {
      title: '满意率(%)',
      dataIndex: 'satisfactionRate',
      width: 140,
    },
    {
      title: 'NPS推荐数',
      dataIndex: 'npsRecommendSize',
      width: 120,
    },
    {
      title: '问题',
      dataIndex: 'questionnaireList',
      width: 300,
      render: renderIssue,
    },
    {
      title: '问题分类',
      dataIndex: 'questionnaireList',
      width: 200,
      render: renderIssueCategory,
    },
    {
      title: '问卷名称',
      dataIndex: 'questionnaireList',
      width: 200,
      render: renderQuestionnaireName,
    },
    {
      title: '评分数量',
      dataIndex: 'questionnaireList',
      width: 150,
      render: renderScoreSize,
    },
    {
      title: '1分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore1Size,
    },
    {
      title: '2分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore2Size,
    },
    {
      title: '3分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore3Size,
    },
    {
      title: '4分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore4Size,
    },
    {
      title: '5分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore5Size,
    },
    {
      title: '6分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore6Size,
    },
    {
      title: '7分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore7Size,
    },
    {
      title: '8分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore8Size,
    },
    {
      title: '9分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore9Size,
    },
    {
      title: '10分数量',
      dataIndex: 'questionnaireList',
      width: 120,
      render: renderScore10Size,
    },
    {
      title: '负向标签',
      dataIndex: 'questionnaireList',
      width: 300,
      render: renderNegativeTag,
    },
  ],
  search: [
    {
      name: 'companyId',
      label: '分公司',
      itemType: 'select',
      key: 'id',
      labelKey: 'name',
      data: 'companyList',
      allowClear: true,
    },
    {
      name: 'date',
      label: '评价时间',
      placeholder: ['评价时间起', '止'],
      span: 140,
      itemType: 'autoDate',
      showTime: 'true',
    },
  ],
  footer: [
    {
      label: '导出',
      type: 'primary',
      status: ['loading:exportLoading'],
      events: ['onClick:onExport'],
    },
  ],
};